import { useEffect, useState } from 'react'
import { disablePush, enablePush, pushState, pushSupported } from '../lib/push'
import type { PushState } from '../lib/push'
import { Button } from './ui/button'

/**
 * NotificationBell is the one control for Web Push: enable it, see that it is
 * on, or learn why it cannot be turned on here.
 *
 * It never prompts on its own — see push.ts for why auto-prompting on sign-in
 * trains users to reflexively deny. This is the explicit, in-context ask:
 * click it, get the browser's real permission dialog, understand why.
 */
export function NotificationBell() {
  const [state, setState] = useState<PushState | 'loading'>('loading')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const refresh = () => {
    if (!pushSupported()) {
      setState('unsupported')
      return
    }
    pushState().then(setState)
  }

  useEffect(() => {
    refresh()
    // Permission can change outside this component too — a user granting or
    // revoking it from the browser's own UI — so it is re-read on focus
    // rather than trusted to stay whatever it was when this mounted.
    window.addEventListener('focus', refresh)
    return () => window.removeEventListener('focus', refresh)
  }, [])

  if (state === 'unsupported') return null

  async function toggle() {
    setBusy(true)
    setError('')
    try {
      if (state === 'on') {
        await disablePush()
      } else {
        await enablePush()
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change notification settings.')
    } finally {
      refresh()
      setBusy(false)
    }
  }

  const label =
    state === 'on'
      ? 'Notifications on — click to turn off'
      : state === 'blocked'
        ? 'Notifications are blocked in your browser settings'
        : 'Enable notifications'

  return (
    <Button
      aria-label={label}
      title={error || label}
      size="icon-sm"
      variant={state === 'on' ? 'default' : 'outline'}
      onClick={toggle}
      disabled={busy || state === 'loading' || state === 'blocked'}
      data-testid="notification-toggle"
      data-permission={state}
    >
      {state === 'on' ? '🔔' : '🔕'}
    </Button>
  )
}
